import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { RequestError } from '@/app/common/types_and_interfaces.ts'
import { FamilyApplicationQueryKeys } from '@/queries/family_applications/queries.ts'
import { PURSE_KEYS } from '@/queries/program_purses/keys.ts'
import {
  fundAwardedStudents,
  FundAwardedStudentsParams,
} from '@/queries/program_purses/requests.ts'
import { FundAwardedStudentsResponse } from '@/queries/program_purses/schemas.ts'

export const useFundAwardedStudents = (
  options?: UseMutationOptions<
    FundAwardedStudentsResponse,
    RequestError,
    FundAwardedStudentsParams
  >
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: fundAwardedStudents,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: PURSE_KEYS.PURSE_DETAILS(
          String(variables.purseId),
          variables.programId
        ),
      })
      queryClient.invalidateQueries({
        queryKey: PURSE_KEYS.PURSES(variables.programId),
      })
      queryClient.invalidateQueries({
        queryKey: ['TRANSACTION_LOGS', variables.programId, variables.purseId],
      })
      // Funded students leave the awarded set, so the preview total is stale immediately.
      queryClient.invalidateQueries({
        queryKey: PURSE_KEYS.AWARDED_STUDENTS_PREVIEW(
          variables.programId,
          variables.purseId
        ),
      })
      // Funded students move from Awarded to Funded, so every applicant/student list has to
      // be refetched. `list_all` is the shared prefix of both the plain and paginated keys.
      queryClient.invalidateQueries({
        queryKey: FamilyApplicationQueryKeys.list_all,
      })

      options?.onSuccess?.(data, variables, context)
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}
